import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import api from "../lib/api";
import { absoluteUrl } from "../context/SiteContext";

/**
 * Reusable image upload field.
 * value: stored URL (absolute or `/api/uploads/...`)
 * onChange(url): called after successful upload OR with empty string on clear.
 * Also accepts pasted external image URL.
 */
export default function ImageUpload({ value, onChange, label = "Image", testid = "image-upload" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlField, setUrlField] = useState(value || "");

  const upload = async (file) => {
    if (!file) return;
    const ok = ["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type);
    if (!ok) {
      toast.error("Unsupported file. Use JPG, PNG, WEBP, or GIF.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB).");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      setUrlField(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testid}>
      <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{label}</label>
      <div className="mt-2 flex flex-col md:flex-row gap-3">
        {/* Pick from device */}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-pick`}
          className="inline-flex items-center gap-2 border border-[#F39C12] text-[#F39C12] px-4 py-2.5 font-bold uppercase tracking-widest text-[10px] hover:bg-[#F39C12]/10 transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Upload from device"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testid}-file`}
        />
        {/* Or paste URL */}
        <div className="flex-1 flex gap-2">
          <input
            type="text"
            value={urlField}
            onChange={(e) => setUrlField(e.target.value)}
            onBlur={() => onChange(urlField)}
            placeholder="...or paste an image URL"
            data-testid={`${testid}-url`}
            className="flex-1 bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-3 py-2.5 outline-none text-sm placeholder:text-[#64748B]"
          />
          {value && (
            <button
              type="button"
              onClick={() => { setUrlField(""); onChange(""); }}
              data-testid={`${testid}-clear`}
              className="border border-[#1F3550] text-[#94A3B8] hover:text-[#EF4444] hover:border-[#EF4444] px-3 transition-colors"
              title="Clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      {value && (
        <div className="mt-3 border border-[#1F3550] bg-[#050A0F] inline-block max-w-full">
          <img
            src={absoluteUrl(value)}
            alt="preview"
            className="max-h-48 max-w-full object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
    </div>
  );
}
